// src/pages/Folder/SingleFolder.tsx

import {
  BusinessTwoTone,
  EditTwoTone,
  FaceTwoTone,
  MessageTwoTone,
  MoreVertTwoTone,
} from '@mui/icons-material'
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import countries from 'i18n-iso-countries'
import de from 'i18n-iso-countries/langs/de.json'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  FolderId,
  FolderImage,
  FolderName,
  FolderShortName,
  FolderSingle,
} from '@/types/domain'

import { SVGStatus } from '../../api'
import {
  AM,
  AMItem,
  Coach,
  ContractStatus,
  FolderNews,
  KPage,
  OfficeHours,
} from '../../components'
import {
  FolderStatus,
  OperatorData,
  useBridgeId,
  useFolder,
  useFolderImage,
  useFolderName,
  useFolderShortName,
  useFolderStatus,
  useOperator,
} from '../../hooks'

const { REACT_APP_API_GOOGLEMAPS } = import.meta.env

countries.registerLocale(de)

export const SingleFolder = () => {
  const navigate = useNavigate()

  const { id, section } = useParams()
  const fId: FolderId = Number(id)
  const fImage: FolderImage = useFolderImage(fId)
  const fName: FolderName = useFolderName(fId)
  const fShort: FolderShortName = useFolderShortName(fId)
  const fStatus: FolderStatus = useFolderStatus(fId)

  const [tab, setTab] = useState(Number(section) || 0)

  const bridgeSensor = useBridgeId(fId)

  const opData: OperatorData = useOperator()

  const fData: FolderSingle = useFolder(fId)

  const renderFolder = () => {
    if (!fId || fId === 0 || !fData) return <Box />

    const { city, country, office_hours, news, sensors, street, zip } = fData

    const RegularMap = null

    if (!sensors) return <Box />

    return (
      <>
        <Tabs
          centered
          indicatorColor={'primary'}
          onChange={(event, tab) => {
            setTab(tab)
            navigate(`/folder/${fId}/${tab}`)
          }}
          scrollButtons={'auto'}
          sx={{ mb: 10 }}
          textColor={'primary'}
          value={tab}
        >
          <Tab value={0} label={'Info'} icon={<BusinessTwoTone />} />
          <Tab value={2} label={'News'} icon={<MessageTwoTone />} disabled />
          {/*<Tab value={3} label={'Notfall'} icon={<LocalHospitalTwoTone />} />*/}
          <Tab value={4} label={'Coach'} icon={<FaceTwoTone />} />
        </Tabs>

        {/* info */}
        <Collapse in={tab === 0}>
          <Collapse in={true}>
            <Typography component={'h4'} variant={'h4'}>
              {fName}
            </Typography>
            <Typography component={'h6'} variant={'h6'}>
              {street}
            </Typography>
            <Typography component={'h6'} variant={'h6'}>
              {zip} {city}
              <br />
              {countries.getName(country, 'de')}
            </Typography>
            {RegularMap && (
              <RegularMap
                googleMapURL={REACT_APP_API_GOOGLEMAPS}
                loadingElement={<div style={{ height: `100%` }} />}
                containerElement={
                  <div
                    style={{
                      height: `280px`,
                      borderRadius: '6px',
                      overflow: 'hidden',
                      border: 'solid 1px black',
                    }}
                  />
                }
                mapElement={<div style={{ height: `100%` }} />}
              />
            )}
            <OfficeHours data={office_hours} />

            <ContractStatus id={fId} />
          </Collapse>
        </Collapse>

        {/* news */}
        <Collapse in={tab === 2}>
          <FolderNews news={news} />
        </Collapse>

        {/* coach */}
        <Collapse in={tab === 4}>
          <Coach bridgeId={bridgeSensor} />
        </Collapse>
      </>
    )
  }

  const renderSubFolder = () =>
    fData?.folders?.length > 0 ? (
      <Collapse>
        <FormControl sx={{ mb: 5, mt: 10 }} fullWidth>
          <Typography component={'h6'} variant={'h6'}>
            Niederlassungen
          </Typography>
        </FormControl>

        <List>
          {fData.folders.map(item => {
            const { city, entity_id, image_url, name, street, zip } = item

            return (
              <ListItemButton
                key={entity_id}
                onClick={() => navigate(`/folder/${entity_id}`)}
              >
                <ListItemAvatar>
                  <Avatar src={image_url} sx={{ width: 40, height: 40 }} />
                </ListItemAvatar>

                <ListItemText
                  primary={name}
                  secondary={`${street}, ${zip} ${city}`}
                />

                <IconButton
                  edge={'end'}
                  onClick={() => navigate(`/folder/${entity_id}/edit`)}
                >
                  <EditTwoTone />
                </IconButton>
              </ListItemButton>
            )
          })}
        </List>
      </Collapse>
    ) : (
      <Box />
    )

  const [actionMenu, showActionMenu] = useState(null)
  const isPageLoading = !fData || (fData && Number(fData.entity_id) !== fId)

  return (
    <>
      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'Gebäude'}
        open={Boolean(actionMenu)}
        onClose={() => showActionMenu(null)}
      >
        {[
          opData?.isAdmin && {
            caption: 'Bearbeiten',
            icon: <EditTwoTone />,
            fn: () => navigate(`/folder/${fId}/edit`),
          },
          null,
          {
            caption: 'Leistung',
            icon: (
              <SVGStatus type={'performance'} status={fStatus.performance} />
            ),
            fn: () => navigate(`/folder/${fId}/home/performance`),
          },
          {
            caption: 'Anlagen',
            icon: (
              <SVGStatus type={'installation'} status={fStatus.installation} />
            ),
            fn: () => navigate(`/folder/${fId}/home/installation`),
          },
          {
            caption: 'Korrelation',
            icon: (
              <SVGStatus type={'correlation'} status={fStatus.correlation} />
            ),
            fn: () => navigate(`/folder/${fId}/home/correlation`),
          },
          {
            caption: 'Sicherheit',
            icon: <SVGStatus type={'security'} status={fStatus.security} />,
            fn: () => navigate(`/folder/${fId}/home/security`),
          },
          {
            caption: 'Wohlbefinden',
            icon: <SVGStatus type={'feeling'} status={fStatus.feeling} />,
            fn: () => navigate(`/folder/${fId}/home/feeling`),
          },
        ].map(item =>
          item ? (
            <AMItem
              caption={item.caption}
              icon={
                <Stack alignItems={'center'} sx={{ width: 25 }}>
                  {item.icon}
                </Stack>
              }
              key={item?.caption}
              onClick={item.fn}
            />
          ) : (
            <Divider key={item?.caption} />
          )
        )}
      </AM>

      <KPage
        avatar={
          <Avatar>
            <Box
              component={'img'}
              src={fImage}
              sx={{ width: 40, height: 40 }}
            />
          </Avatar>
        }
        action={
          <IconButton onClick={event => showActionMenu(event.currentTarget)}>
            <MoreVertTwoTone />
          </IconButton>
        }
        isLoading={isPageLoading}
        subheader={''}
        title={fName + (fShort !== null ? ` (${fShort})` : '')}
      >
        {renderFolder()}
        {renderSubFolder()}
      </KPage>
    </>
  )
}
